import React, { useState } from "react";
import { cn } from "../ui";

const sizes = {
  xs: "size-7 text-xs",
  sm: "size-8 text-xs",
  md: "size-10 text-meta",
  lg: "size-12 text-sm",
  xl: "size-14 text-base",
} as const;

export interface AvatarProps {
  src?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Full name or email, used when first/last are not available */
  name?: string | null;
  size?: keyof typeof sizes;
  className?: string;
}

const initialsFor = (firstName?: string | null, lastName?: string | null, name?: string | null) => {
  const first = firstName?.trim();
  const last = lastName?.trim();
  if (first || last) return `${first?.charAt(0) ?? ""}${last?.charAt(0) ?? ""}`.toUpperCase();
  const raw = (name ?? "").trim();
  // An email: take initials from the local part ("alex.porter@x.com" -> "AP", "rebecca@x.com" -> "RE")
  if (raw.includes("@")) {
    const local = raw.split("@")[0];
    const bits = local.split(/[._\-+]+/).filter(Boolean);
    if (bits.length >= 2) return `${bits[0].charAt(0)}${bits[1].charAt(0)}`.toUpperCase();
    return (bits[0] ?? local).slice(0, 2).toUpperCase() || "?";
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  return parts[0]?.charAt(0).toUpperCase() || "?";
};

/**
 * Person avatar for network, inbox and contact lists: photo when available,
 * neutral initials tile otherwise (falls back if the photo fails to load).
 */
export const Avatar: React.FC<AvatarProps> = ({ src, firstName, lastName, name, size = "md", className }) => {
  const [failed, setFailed] = useState(false);
  const label = [firstName, lastName].filter(Boolean).join(" ").trim() || name || "User";

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={label}
        onError={() => setFailed(true)}
        className={cn("shrink-0 rounded-full object-cover ring-1 ring-gray-200", sizes[size], className)}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center rounded-full bg-gray-100 font-semibold tracking-tight text-gray-600 ring-1 ring-inset ring-gray-200",
        sizes[size],
        className
      )}
    >
      {initialsFor(firstName, lastName, name)}
    </span>
  );
};

export default Avatar;
